import React, { useState, useEffect, useRef } from 'react';
import supabase from './supabaseClient';
import './App.css';

const EMPTY_MANUAL = {
  type: 'flight',
  airline: '', flight_number: '', departure_airport: '', arrival_airport: '',
  departure_time: '', arrival_time: '', confirmation_code: '', passenger_name: '', seat: '',
  name: '', address: '', check_in: '', check_out: '', location: '', start_time: '', end_time: '', notes: ''
};

const TIMEZONES = [
  { label: 'Vancouver / Seattle (PT)', value: 'America/Vancouver' },
  { label: 'Denver (MT)', value: 'America/Denver' },
  { label: 'Chicago (CT)', value: 'America/Chicago' },
  { label: 'New York / Toronto (ET)', value: 'America/New_York' },
  { label: 'London (GMT/BST)', value: 'Europe/London' },
  { label: 'Paris / Berlin / Rome (CET)', value: 'Europe/Paris' },
  { label: 'Helsinki / Athens (EET)', value: 'Europe/Helsinki' },
  { label: 'Reykjavik (GMT, no DST)', value: 'Atlantic/Reykjavik' },
  { label: 'Nairobi / East Africa (EAT)', value: 'Africa/Nairobi' },
  { label: 'Johannesburg / South Africa (SAST)', value: 'Africa/Johannesburg' },
  { label: 'Dubai (GST)', value: 'Asia/Dubai' },
  { label: 'Bangkok (ICT)', value: 'Asia/Bangkok' },
  { label: 'Singapore / HK (SGT)', value: 'Asia/Singapore' },
  { label: 'Tokyo (JST)', value: 'Asia/Tokyo' },
  { label: 'Sydney (AEDT)', value: 'Australia/Sydney' },
  { label: 'Auckland (NZST)', value: 'Pacific/Auckland' },
  { label: 'Honolulu (HST)', value: 'Pacific/Honolulu' },
];

function parseLocalTime(dt) {
  if (!dt) return null;
  const [datePart, timePart] = dt.split('T');
  if (!datePart) return null;
  const [year, month, day] = datePart.split('-');
  const [hour, minute] = (timePart || '00:00').split(':');
  return { year, month: parseInt(month), day: parseInt(day), hour: parseInt(hour), minute };
}

function formatLocalTime(dt) {
  const p = parseLocalTime(dt);
  if (!p) return '—';
  const ampm = p.hour >= 12 ? 'PM' : 'AM';
  const h12 = p.hour % 12 || 12;
  return `${h12}:${p.minute} ${ampm}`;
}

function formatLocalDateTime(dt) {
  const p = parseLocalTime(dt);
  if (!p) return '—';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const ampm = p.hour >= 12 ? 'PM' : 'AM';
  const h12 = p.hour % 12 || 12;
  return `${months[p.month-1]} ${p.day}, ${h12}:${p.minute} ${ampm}`;
}

function formatTripDate(d) {
  if (!d) return '—';
  const [year, month, day] = d.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatDayHeading(dateStr) {
  const [year, month, day] = dateStr.split('-');
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const d = new Date(parseInt(year), parseInt(month)-1, parseInt(day));
  return `${days[d.getDay()]}, ${months[parseInt(month)-1]} ${parseInt(day)}`;
}

function App() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [authError, setAuthError] = useState('');
  const [authMode, setAuthMode] = useState('signin');
  const [signingIn, setSigningIn] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuView, setMenuView] = useState('main');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [accountMsg, setAccountMsg] = useState('');
  const [sharedTrips, setSharedTrips] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const menuRef = useRef(null);

  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [flights, setFlights] = useState([]);
  const [lodging, setLodging] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTripName, setNewTripName] = useState('');
  const [newTripDestination, setNewTripDestination] = useState('');
  const [newTripStart, setNewTripStart] = useState('');
  const [newTripEnd, setNewTripEnd] = useState('');
  const [newTripTimezone, setNewTripTimezone] = useState('America/Vancouver');
  const [showNewTripForm, setShowNewTripForm] = useState(false);
  const [reassigning, setReassigning] = useState(null);
  const [timelineOpen, setTimelineOpen] = useState(true);
  const [editingTrip, setEditingTrip] = useState(null);
  const [editValues, setEditValues] = useState({ name: '', destination: '', start_date: '', end_date: '', timezone: 'America/Vancouver' });
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualEntry, setManualEntry] = useState(EMPTY_MANUAL);
  const [sharingTrip, setSharingTrip] = useState(null);
  const [shareUsername, setShareUsername] = useState('');
  const [shareRole, setShareRole] = useState('viewer');
  const [shareError, setShareError] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareSuccess, setShareSuccess] = useState('');

  useEffect(() => {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) {
      fetchTrips();
      fetchUserProfile();
      fetchSharedTrips();
      fetchUnassigned();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useEffect(() => {
    if (selectedTrip) fetchBookings(selectedTrip.id);
  }, [selectedTrip]);

  async function fetchUserProfile() {
    const { data } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', session.user.id)
      .single();
    setUserProfile(data);
    setNewDisplayName(data?.display_name || '');
  }

  async function fetchSharedTrips() {
  const { data } = await supabase
    .from('trip_members')
    .select('trip_id, role, shared_by_user_id, trips(*)')
    .eq('user_id', session.user.id);
  if (data) {
    const withNames = await Promise.all(data.map(async m => {
      if (m.shared_by_user_id) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('display_name, trippit_username')
          .eq('user_id', m.shared_by_user_id)
          .single();
        return { ...m, sharedByName: profile?.display_name || profile?.trippit_username || 'Someone' };
      }
      return { ...m, sharedByName: null };
    }));
    setSharedTrips(withNames);
  }
}

  async function saveDisplayName() {
    const { error } = await supabase
      .from('user_profiles')
      .update({ display_name: newDisplayName })
      .eq('user_id', session.user.id);
    if (!error) {
      setUserProfile(prev => ({ ...prev, display_name: newDisplayName }));
      setAccountMsg('Display name updated!');
      setTimeout(() => setAccountMsg(''), 3000);
    }
  }

  async function savePassword() {
    if (!newPassword || newPassword.length < 6) {
      setAccountMsg('Password must be at least 6 characters');
      return;
    }
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (!error) {
      setNewPassword('');
      setAccountMsg('Password updated!');
      setTimeout(() => setAccountMsg(''), 3000);
    } else {
      setAccountMsg(error.message);
    }
  }

  async function signIn() {
    setSigningIn(true);
    setAuthError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setAuthError(error.message);
    setSigningIn(false);
  }

  async function signUp() {
    setSigningIn(true);
    setAuthError('');
    if (!username.trim()) { setAuthError('Username is required'); setSigningIn(false); return; }
    if (!/^[a-z0-9_]+$/.test(username)) { setAuthError('Username can only contain lowercase letters, numbers, and underscores'); setSigningIn(false); return; }
    const { data: existing } = await supabase.from('user_profiles').select('id').eq('trippit_username', username).single();
    if (existing) { setAuthError('That username is already taken'); setSigningIn(false); return; }
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) { setAuthError(error.message); setSigningIn(false); return; }
    if (data.user) {
      await supabase.from('user_profiles').insert([{ user_id: data.user.id, trippit_username: username.toLowerCase() }]);
    }
    setSigningIn(false);
  }

  async function sendMagicLink() {
    setSigningIn(true);
    setAuthError('');
    const { error } = await supabase.auth.signInWithOtp({ email });
    if (error) {
      setAuthError(error.message);
    } else {
      setMagicLinkSent(true);
    }
    setSigningIn(false);
  }

  async function signOut() {
    await supabase.auth.signOut();
    setTrips([]); setSelectedTrip(null);
    setFlights([]); setLodging([]); setActivities([]);
    setUserProfile(null); setMenuOpen(false);
  }

  async function fetchTrips() {
    setLoading(true);
    const { data, error } = await supabase.from('trips').select('*').order('start_date', { ascending: true });
    if (!error) setTrips(data);
    setLoading(false);
  }

  async function fetchUnassigned() {
    const [f, l, a] = await Promise.all([
      supabase.from('flights').select('*').is('trip_id', null).eq('user_id', session.user.id),
      supabase.from('lodging').select('*').is('trip_id', null).eq('user_id', session.user.id),
      supabase.from('activities').select('*').is('trip_id', null).eq('user_id', session.user.id),
    ]);
    const items = [
      ...(f.data || []).map(x => ({ ...x, _table: 'flights', _label: `✈️ ${x.airline || ''} ${x.flight_number || ''} — ${x.departure_airport || '?'}→${x.arrival_airport || '?'}` })),
      ...(l.data || []).map(x => ({ ...x, _table: 'lodging', _label: `🏨 ${x.name || 'Lodging'}` })),
      ...(a.data || []).map(x => ({ ...x, _table: 'activities', _label: `🎯 ${x.name || 'Activity'}` })),
    ];
    setUnassigned(items);
  }

  async function assignUnassigned(table, id, tripId) {
    if (!tripId) return;
    await supabase.from(table).update({ trip_id: tripId }).eq('id', id);
    setUnassigned(prev => prev.filter(x => x.id !== id));
    if (selectedTrip?.id === tripId) fetchBookings(tripId);
  }

  async function fetchBookings(tripId) {
    const [flightsRes, lodgingRes, activitiesRes] = await Promise.all([
      supabase.from('flights').select('*').eq('trip_id', tripId).order('departure_time'),
      supabase.from('lodging').select('*').eq('trip_id', tripId).order('check_in'),
      supabase.from('activities').select('*').eq('trip_id', tripId).order('start_time'),
    ]);
    if (!flightsRes.error) setFlights([...flightsRes.data].sort((a, b) => (a.departure_time || '').localeCompare(b.departure_time || '')));
    if (!lodgingRes.error) setLodging([...lodgingRes.data].sort((a, b) => (a.check_in || '').localeCompare(b.check_in || '')));
    if (!activitiesRes.error) setActivities([...activitiesRes.data].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')));
  }

  async function createTrip() {
    const { data, error } = await supabase.from('trips').insert([{
      name: newTripName, destination: newTripDestination,
      start_date: newTripStart, end_date: newTripEnd,
      timezone: newTripTimezone, user_id: session.user.id
    }]).select().single();
    if (!error) {
      setTrips([...trips, data]);
      setSelectedTrip(data);
      setShowNewTripForm(false);
      setNewTripName(''); setNewTripDestination(''); setNewTripStart(''); setNewTripEnd('');
      setNewTripTimezone('America/Vancouver');
    }
  }

  async function saveEditTrip() {
    if (!editValues.name.trim()) return;
    const { error } = await supabase.from('trips').update({
      name: editValues.name, destination: editValues.destination,
      start_date: editValues.start_date, end_date: editValues.end_date, timezone: editValues.timezone,
    }).eq('id', editingTrip);
    if (!error) {
      await fetchTrips();
      if (selectedTrip?.id === editingTrip) setSelectedTrip(prev => ({ ...prev, ...editValues }));
    }
    setEditingTrip(null);
  }

  async function deleteTrip(tripId) {
    if (!window.confirm('Are you sure you want to delete this trip and all its bookings? This cannot be undone.')) return;
    await supabase.from('flights').delete().eq('trip_id', tripId);
    await supabase.from('lodging').delete().eq('trip_id', tripId);
    await supabase.from('activities').delete().eq('trip_id', tripId);
    await supabase.from('trips').delete().eq('id', tripId);
    setTrips(trips.filter(t => t.id !== tripId));
    if (selectedTrip?.id === tripId) { setSelectedTrip(null); setFlights([]); setLodging([]); setActivities([]); }
  }

  async function shareTrip() {
    setShareError(''); setShareSuccess('');
    if (!shareUsername.trim()) { setShareError('Please enter an email or username'); return; }
    setShareLoading(true);

    const sharedByName = userProfile?.display_name || userProfile?.trippit_username || 'Someone';

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/share-trip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({
  emailOrUsername: shareUsername.trim(),
  tripId: sharingTrip,
  role: shareRole,
  sharedByName,
  sharedByUserId: session.user.id
})
      });

      const result = await response.json();

      if (!response.ok) {
        setShareError(result.error || 'Something went wrong');
      } else {
        setShareSuccess(`Trip shared with ${shareUsername}!`);
        setShareUsername('');
      }
    } catch (err) {
      setShareError('Could not connect to server');
    }
    setShareLoading(false);
  }

  async function reassignBooking(table, id, newTripId) {
    await supabase.from(table).update({ trip_id: newTripId }).eq('id', id);
    fetchBookings(selectedTrip.id);
  }

  async function deleteBooking(table, id) {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    await supabase.from(table).delete().eq('id', id);
    fetchBookings(selectedTrip.id);
  }

  async function saveManualEntry() {
    const tripId = selectedTrip.id;
    const type = manualEntry.type;
    let error = null;
    if (type === 'flight') {
      ({ error } = await supabase.from('flights').insert([{
        trip_id: tripId, airline: manualEntry.airline, flight_number: manualEntry.flight_number,
        departure_airport: manualEntry.departure_airport, arrival_airport: manualEntry.arrival_airport,
        departure_time: manualEntry.departure_time || null, arrival_time: manualEntry.arrival_time || null,
        confirmation_code: manualEntry.confirmation_code, passenger_name: manualEntry.passenger_name,
        seat: manualEntry.seat, is_duplicate: false
      }]));
    } else if (type === 'lodging') {
      ({ error } = await supabase.from('lodging').insert([{
        trip_id: tripId, name: manualEntry.name, address: manualEntry.address,
        check_in: manualEntry.check_in || null, check_out: manualEntry.check_out || null,
        confirmation_code: manualEntry.confirmation_code, notes: manualEntry.notes, is_duplicate: false
      }]));
    } else if (type === 'activity') {
      ({ error } = await supabase.from('activities').insert([{
        trip_id: tripId, name: manualEntry.name, location: manualEntry.location,
        start_time: manualEntry.start_time || null, end_time: manualEntry.end_time || null,
        confirmation_code: manualEntry.confirmation_code, notes: manualEntry.notes, is_duplicate: false
      }]));
    }
    if (!error) { fetchBookings(tripId); setShowManualForm(false); setManualEntry(EMPTY_MANUAL); }
    else alert('Error saving entry: ' + error.message);
  }

  function set(field, value) { setManualEntry(prev => ({ ...prev, [field]: value })); }

  function getTzLabel() {
    const tz = selectedTrip?.timezone || 'America/Vancouver';
    const match = TIMEZONES.find(t => t.value === tz);
    return match ? match.label.match(/\((.+)\)/)?.[1] || '' : '';
  }

   function buildGroupedTimeline() {
    const allEvents = [
      ...flights.map(f => ({
        time: f.departure_time,
        endTime: f.arrival_time,
        label: `✈️ ${f.airline} ${f.flight_number} — ${f.departure_airport} → ${f.arrival_airport}`,
        subLabel: `Departs ${formatLocalTime(f.departure_time)}, arrives ${formatLocalTime(f.arrival_time)}`,
        color: '#2563eb', background: '#eff6ff', border: '#bfdbfe'
      })),
      ...lodging.map(l => ({ time: l.check_in, endTime: null, isBoundary: true, label: `🏨 Check in: ${l.name}`, subLabel: l.address || '', color: '#16a34a', background: '#f0fdf4', border: '#bbf7d0' })),
      ...lodging.map(l => ({ time: l.check_out, endTime: null, isBoundary: true, label: `🏨 Check out: ${l.name}`, subLabel: '', color: '#16a34a', background: '#f0fdf4', border: '#bbf7d0' })),
      ...activities.map(a => ({ time: a.start_time, endTime: a.end_time || null, label: `🎯 ${a.name}`, subLabel: a.location || a.notes || '', color: '#7c3aed', background: '#faf5ff', border: '#e9d5ff' })),
    ].filter(e => e.time).sort((a, b) => {
      const aTime = a.time.replace('T', '').replace(/-/g, '').replace(/:/g, '');
      const bTime = b.time.replace('T', '').replace(/-/g, '').replace(/:/g, '');
      return aTime.localeCompare(bTime);
    });

    const grouped = {};
    allEvents.forEach(event => {
      const dayStr = event.time.split('T')[0];
      if (!grouped[dayStr]) grouped[dayStr] = [];
      grouped[dayStr].push(event);
    });

    // Overlap detection: only real fixed-duration events (flights, activities) can conflict.
    // Hotel check-in/check-out are min/max boundary times, not fixed events — skip them entirely.
    Object.values(grouped).forEach(dayEvents => {
      for (let i = 0; i < dayEvents.length; i++) {
        if (dayEvents[i].isBoundary) continue;
        for (let j = 0; j < i; j++) {
          const prior = dayEvents[j];
          if (prior.isBoundary) continue;
          if (prior.endTime && prior.endTime > dayEvents[i].time) {
            dayEvents[i].overlaps = true;
            prior.overlaps = true;
          }
        }
      }
    });

    return grouped;
  }

  const groupedTimeline = buildGroupedTimeline();
  const timelineDays = Object.keys(groupedTimeline).sort();
  const todayArchive = new Date();
  todayArchive.setHours(0, 0, 0, 0);
  const isPast = t => t.end_date && new Date(t.end_date) < todayArchive;
  const myTrips = trips.filter(t => t.user_id === session?.user?.id && !isPast(t));
  const archivedTrips = trips.filter(t => t.user_id === session?.user?.id && isPast(t));
const tripsSharedWithMe = sharedTrips.map(s => s.trips).filter(Boolean);

  const inputStyle = { display: 'block', width: '100%', marginBottom: 8, padding: 8, borderRadius: 6, border: '1px solid #ddd', boxSizing: 'border-box' };
  const menuItemStyle = { display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 14, color: '#333', borderRadius: 6 };

  if (authLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ color: '#999' }}>Loading...</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'system-ui, sans-serif', background: '#f5f5f5' }}>
        <div style={{ background: 'white', borderRadius: 16, padding: 32, width: 340, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✈️</div>
            <h1 style={{ fontSize: 22, margin: 0, color: '#1e1b4b' }}>Trippit</h1>
            <p style={{ color: '#999', fontSize: 14, margin: '4px 0 0' }}>Your personal travel planner</p>
          </div>

          <div style={{ display: 'flex', marginBottom: 20, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <button onClick={() => { setAuthMode('signin'); setAuthError(''); setMagicLinkSent(false); }}
              style={{ flex: 1, padding: '8px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: authMode === 'signin' ? '#4f46e5' : 'white', color: authMode === 'signin' ? 'white' : '#666' }}>
              Sign In
            </button>
            <button onClick={() => { setAuthMode('signup'); setAuthError(''); setMagicLinkSent(false); }}
              style={{ flex: 1, padding: '8px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: authMode === 'signup' ? '#4f46e5' : 'white', color: authMode === 'signup' ? 'white' : '#666' }}>
              Sign Up
            </button>
          </div>

          {authMode === 'signup' && (
            <div>
              <input placeholder="Choose a username (e.g. jessica)" value={username} onChange={e => setUsername(e.target.value.toLowerCase())} style={inputStyle} />
              <div style={{ fontSize: 11, color: '#999', marginTop: -6, marginBottom: 8 }}>
                Your forwarding address will be {username || 'username'}@trippit.co
              </div>
            </div>
          )}

          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle}
            onKeyDown={e => e.key === 'Enter' && (authMode === 'signin' ? signIn() : signUp())} />

          {authMode === 'signup' && (
            <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle}
              onKeyDown={e => e.key === 'Enter' && signUp()} />
          )}

          {authMode === 'signin' && (
            <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle}
              onKeyDown={e => e.key === 'Enter' && signIn()} />
          )}

          {authError && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{authError}</div>}

          {magicLinkSent ? (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 12, fontSize: 14, color: '#16a34a', textAlign: 'center' }}>
              ✅ Magic link sent! Check your email and click the link to sign in.
            </div>
          ) : (
            <>
              <button onClick={authMode === 'signin' ? signIn : signUp} disabled={signingIn}
                style={{ width: '100%', padding: '10px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 15, marginBottom: 8 }}>
                {signingIn ? (authMode === 'signin' ? 'Signing in...' : 'Creating account...') : (authMode === 'signin' ? 'Sign In' : 'Create Account')}
              </button>
              {authMode === 'signin' && (
                <button onClick={sendMagicLink} disabled={signingIn || !email}
                  style={{ width: '100%', padding: '10px', background: 'white', color: '#4f46e5', border: '1px solid #4f46e5', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                  ✉️ Email me a magic link instead
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', maxWidth: 800, margin: '0 auto', padding: 24 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h1 style={{ fontSize: 28, margin: 0 }}>✈️ Trippit</h1>

        <div ref={menuRef} style={{ position: 'relative' }}>
          <button onClick={() => { setMenuOpen(!menuOpen); setMenuView('main'); setAccountMsg(''); }}
            style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', padding: '6px 10px', fontSize: 18 }}>
            ☰
          </button>

          {menuOpen && (
            <div style={{ position: 'absolute', right: 0, top: 40, background: 'white', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', width: 280, zIndex: 200, padding: 8 }}>

              {menuView === 'main' && (
                <>
                  {userProfile && (
                    <div style={{ padding: '8px 16px 12px', borderBottom: '1px solid #f0f0f0', marginBottom: 4 }}>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{userProfile.display_name || userProfile.trippit_username}</div>
                      <div style={{ fontSize: 12, color: '#999' }}>@{userProfile.trippit_username}</div>
                    </div>
                  )}
                  <button style={menuItemStyle} onClick={() => setMenuView('account')}>👤 Account</button>
                  <button style={menuItemStyle} onClick={() => { setMenuView('shared'); fetchSharedTrips(); }}>🔗 Shared Trips</button>
                  <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 4, paddingTop: 4 }}>
                    <button style={{ ...menuItemStyle, color: '#ef4444' }} onClick={signOut}>Sign Out</button>
                  </div>
                </>
              )}

              {menuView === 'account' && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px 12px', borderBottom: '1px solid #f0f0f0', marginBottom: 8 }}>
                    <button onClick={() => setMenuView('main')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 0 }}>←</button>
                    <strong>Account</strong>
                  </div>
                  <div style={{ padding: '0 16px 16px' }}>
                    <div style={{ background: '#f5f5f5', borderRadius: 8, padding: 10, marginBottom: 16 }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Your forwarding address</div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{userProfile?.trippit_username}@trippit.co</div>
                    </div>
                    <label style={{ fontSize: 12, color: '#666' }}>Display name</label>
                    <input value={newDisplayName} onChange={e => setNewDisplayName(e.target.value)} placeholder="Your name" style={inputStyle} />
                    <button onClick={saveDisplayName}
                      style={{ width: '100%', padding: '8px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, marginBottom: 16 }}>
                      Save Name
                    </button>
                    <label style={{ fontSize: 12, color: '#666' }}>New password</label>
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="At least 6 characters" style={inputStyle} />
                    <button onClick={savePassword}
                      style={{ width: '100%', padding: '8px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                      Update Password
                    </button>
                    {accountMsg && <div style={{ marginTop: 10, fontSize: 13, color: accountMsg.includes('!') ? '#16a34a' : '#ef4444' }}>{accountMsg}</div>}
                  </div>
                </>
              )}

              {menuView === 'shared' && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px 12px', borderBottom: '1px solid #f0f0f0', marginBottom: 8 }}>
                    <button onClick={() => setMenuView('main')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 0 }}>←</button>
                    <strong>Shared Trips</strong>
                  </div>
                  <div style={{ padding: '0 16px 16px' }}>
                    {sharedTrips.length === 0 ? (
                      <div style={{ color: '#999', fontSize: 14 }}>No trips have been shared with you yet.</div>
                    ) : (
                      sharedTrips.map(m => (
                        <div key={m.trip_id} style={{ marginBottom: 8 }}>
                          <button
                            onClick={() => { setSelectedTrip(m.trips); setMenuOpen(false); }}
                            style={{ display: 'block', width: '100%', padding: '10px', background: '#f5f5f5', border: 'none', borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{m.trips.name}</div>
                            <div style={{ fontSize: 12, color: '#999' }}>{m.trips.destination} · {m.role}</div>
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {userProfile && (
        <p style={{ color: '#666', marginBottom: 24 }}>
          Forward booking confirmations to <strong>{userProfile.trippit_username}@trippit.co</strong>
        </p>
      )}

      {/* Trip selector */}
      <div style={{ marginBottom: 24 }}>
        {unassigned.length > 0 && (
          <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#f57f17', marginBottom: 10 }}>
              📥 Unassigned bookings ({unassigned.length}) — pick a trip for each
            </div>
            {unassigned.map(item => (
              <div key={item.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200, fontSize: 14 }}>{item._label}</div>
                <select defaultValue="" onChange={e => assignUnassigned(item._table, item.id, e.target.value)}
                  style={{ padding: 8, borderRadius: 6, border: '1px solid #ddd' }}>
                  <option value="" disabled>Assign to trip…</option>
                  {myTrips.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <button onClick={async () => { await supabase.from(item._table).delete().eq('id', item.id); setUnassigned(prev => prev.filter(x => x.id !== item.id)); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 13 }}>Delete</button>
              </div>
            ))}
          </div>
        )}

        {/* My Trips */}
        {myTrips.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>My Trips</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {myTrips.map(trip => (
                <div key={trip.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button onClick={() => setSelectedTrip(trip)}
  style={{ padding: '8px 16px', borderRadius: 20, border: '2px solid #4f46e5', background: selectedTrip?.id === trip.id ? '#4f46e5' : 'white', color: selectedTrip?.id === trip.id ? 'white' : '#4f46e5', cursor: 'pointer', fontWeight: 600, textAlign: 'left' }}>
  <div>{trip.name}</div>
  {(trip.start_date || trip.end_date) && (
    <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, marginTop: 2 }}>
      {trip.start_date ? formatTripDate(trip.start_date) : ''}{trip.end_date ? ` – ${formatTripDate(trip.end_date)}` : ''}
    </div>
  )}
</button>
                  <button onClick={() => { setEditingTrip(trip.id); setEditValues({ name: trip.name, destination: trip.destination || '', start_date: trip.start_date || '', end_date: trip.end_date || '', timezone: trip.timezone || 'America/Vancouver' }); }}
                    title="Edit trip" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#999', padding: '0 2px' }}>✏️</button>
                  <button onClick={() => { setSharingTrip(trip.id); setShareUsername(''); setShareError(''); setShareSuccess(''); }}
                    title="Share trip" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#999', padding: '0 2px' }}>🔗</button>
                  <button onClick={() => deleteTrip(trip.id)} title="Delete trip"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#999', padding: '0 2px' }}>🗑️</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shared Trips */}
        {tripsSharedWithMe.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Shared Trips</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            
              {tripsSharedWithMe.map(trip => (
  <div key={trip.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    <button onClick={() => setSelectedTrip(trip)}
      style={{ padding: '8px 16px', borderRadius: 20, border: '2px solid #7c3aed', background: selectedTrip?.id === trip.id ? '#7c3aed' : 'white', color: selectedTrip?.id === trip.id ? 'white' : '#7c3aed', cursor: 'pointer', fontWeight: 600, textAlign: 'left' }}>
      <div>{trip.name} 👤</div>
      <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, marginTop: 2 }}>
        {(() => {
          const member = sharedTrips.find(s => s.trip_id === trip.id);
          const name = member?.sharedByName;
          const dates = [trip.start_date ? formatTripDate(trip.start_date) : '', trip.end_date ? formatTripDate(trip.end_date) : ''].filter(Boolean).join(' – ');
          return [name ? `Shared by ${name}` : null, dates].filter(Boolean).join(' · ');
        })()}
      </div>
    </button>
  </div>
))}
            </div>
          </div>
        )}

        <button onClick={() => setShowNewTripForm(!showNewTripForm)}
          style={{ padding: '10px 20px', borderRadius: 20, border: 'none', background: '#4f46e5', color: 'white', cursor: 'pointer', fontWeight: 700, fontSize: 15, boxShadow: '0 2px 8px rgba(79,70,229,0.3)' }}>
          + New Trip
        </button>
        {archivedTrips.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <button onClick={() => setShowArchived(!showArchived)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, padding: 0 }}>
              {showArchived ? '▼' : '▶'} Archived Trips ({archivedTrips.length})
            </button>
            {showArchived && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {archivedTrips.map(trip => (
                  <div key={trip.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button onClick={() => setSelectedTrip(trip)}
                      style={{ padding: '8px 16px', borderRadius: 20, border: '2px solid #ccc', background: selectedTrip?.id === trip.id ? '#ccc' : 'white', color: selectedTrip?.id === trip.id ? 'white' : '#999', cursor: 'pointer', fontWeight: 600, textAlign: 'left' }}>
                      <div>{trip.name}</div>
                      {(trip.start_date || trip.end_date) && (
                        <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, marginTop: 2 }}>
                          {trip.start_date ? formatTripDate(trip.start_date) : ''}{trip.end_date ? ` – ${formatTripDate(trip.end_date)}` : ''}
                        </div>
                      )}
                    </button>
                    <button onClick={() => { setEditingTrip(trip.id); setEditValues({ name: trip.name, destination: trip.destination || '', start_date: trip.start_date || '', end_date: trip.end_date || '', timezone: trip.timezone || 'America/Vancouver' }); }}
                      title="Edit trip" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#999', padding: '0 2px' }}>✏️</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Edit trip modal */}
        {editingTrip && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: 'white', borderRadius: 12, padding: 24, width: 340 }}>
              <h3 style={{ margin: '0 0 16px' }}>Edit Trip</h3>
              <label style={{ fontSize: 12, color: '#666' }}>Trip name</label>
              <input value={editValues.name} onChange={e => setEditValues(v => ({ ...v, name: e.target.value }))} style={inputStyle} autoFocus />
              <label style={{ fontSize: 12, color: '#666' }}>Destination</label>
              <input value={editValues.destination} onChange={e => setEditValues(v => ({ ...v, destination: e.target.value }))} style={inputStyle} />
              <label style={{ fontSize: 12, color: '#666' }}>Start date</label>
              <input type="date" value={editValues.start_date} onChange={e => setEditValues(v => ({ ...v, start_date: e.target.value }))} style={inputStyle} />
              <label style={{ fontSize: 12, color: '#666' }}>End date</label>
              <input type="date" value={editValues.end_date} onChange={e => setEditValues(v => ({ ...v, end_date: e.target.value }))} style={inputStyle} />
              <label style={{ fontSize: 12, color: '#666' }}>Timezone</label>
              <select value={editValues.timezone} onChange={e => setEditValues(v => ({ ...v, timezone: e.target.value }))} style={{ ...inputStyle, marginTop: 4 }}>
                {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
              </select>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={saveEditTrip} style={{ flex: 1, padding: '8px 16px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Save</button>
                <button onClick={() => setEditingTrip(null)} style={{ flex: 1, padding: '8px 16px', background: '#f5f5f5', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Share trip modal */}
        {sharingTrip && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: 'white', borderRadius: 12, padding: 24, width: 340 }}>
              <h3 style={{ margin: '0 0 8px' }}>Share Trip</h3>
              <p style={{ fontSize: 13, color: '#666', margin: '0 0 16px' }}>Enter the email address or Trippit username of the person you want to share with.</p>
              <label style={{ fontSize: 12, color: '#666' }}>Email or username</label>
              <input placeholder="e.g. jason or jason@email.com" value={shareUsername} onChange={e => setShareUsername(e.target.value.toLowerCase())} style={inputStyle} autoFocus />
              <label style={{ fontSize: 12, color: '#666' }}>Permission</label>
              <select value={shareRole} onChange={e => setShareRole(e.target.value)} style={{ ...inputStyle, marginTop: 4 }}>
                <option value="viewer">Viewer — can see the trip</option>
                <option value="editor">Editor — can add and edit bookings</option>
              </select>
              {shareError && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{shareError}</div>}
              {shareSuccess && <div style={{ color: '#16a34a', fontSize: 13, marginBottom: 8 }}>{shareSuccess}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={shareTrip} disabled={shareLoading} style={{ flex: 1, padding: '8px 16px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, opacity: shareLoading ? 0.7 : 1 }}>
                  {shareLoading ? 'Sharing...' : 'Share'}
                </button>
                <button onClick={() => setSharingTrip(null)} style={{ flex: 1, padding: '8px 16px', background: '#f5f5f5', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Done</button>
              </div>
            </div>
          </div>
        )}

        {showNewTripForm && (
          <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 12, marginTop: 8 }}>
            <input placeholder="Trip name" value={newTripName} onChange={e => setNewTripName(e.target.value)} style={inputStyle} />
            <input placeholder="Destination" value={newTripDestination} onChange={e => setNewTripDestination(e.target.value)} style={inputStyle} />
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input type="date" value={newTripStart} onChange={e => setNewTripStart(e.target.value)} style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #ddd' }} />
              <input type="date" value={newTripEnd} onChange={e => setNewTripEnd(e.target.value)} style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #ddd' }} />
            </div>
            <label style={{ fontSize: 12, color: '#666' }}>Destination timezone</label>
            <select value={newTripTimezone} onChange={e => setNewTripTimezone(e.target.value)} style={{ ...inputStyle, marginTop: 4 }}>
              {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
            </select>
            <button onClick={createTrip} disabled={!newTripName}
              style={{ padding: '8px 20px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
              Create Trip
            </button>
          </div>
        )}
      </div>

      {selectedTrip && (
        <>
          <h2 style={{ fontSize: 22, marginBottom: 4 }}>{selectedTrip.name}</h2>
          <p style={{ color: '#666', marginBottom: 24 }}>
            {selectedTrip.destination} · {formatTripDate(selectedTrip.start_date)} – {formatTripDate(selectedTrip.end_date)}
            {getTzLabel() && <span style={{ marginLeft: 8, fontSize: 12, background: '#e0e7ff', color: '#4f46e5', borderRadius: 4, padding: '2px 6px' }}>{getTzLabel()}</span>}
          </p>

          {timelineDays.length > 0 && (
            <div style={{ background: '#f0f4ff', borderRadius: 12, marginBottom: 24, overflow: 'hidden' }}>
              <button onClick={() => setTimelineOpen(!timelineOpen)}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 600, color: '#1e1b4b' }}>
                <span>📅 Trip at a Glance</span>
                <span style={{ fontSize: 12, color: '#6366f1' }}>{timelineOpen ? '▲ Hide' : '▼ Show'}</span>
              </button>
              {timelineOpen && (
                <div style={{ padding: '0 16px 16px' }}>
                  {timelineDays.map(day => (
                    <div key={day} style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#4f46e5', borderBottom: '1px solid #c7d2fe', paddingBottom: 4, marginBottom: 8 }}>
                        {formatDayHeading(day)}
                      </div>
                      {groupedTimeline[day].map((event, i) => (
                        <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 6, alignItems: 'flex-start', background: event.overlaps ? '#fff3cd' : event.background, border: `1px solid ${event.overlaps ? '#ffc107' : event.border}`, borderRadius: 8, padding: '6px 10px' }}>
                          <span style={{ color: event.color, fontWeight: 600, minWidth: 70, fontSize: 13 }}>{formatLocalTime(event.time)}</span>
                          <div>
                          <div style={{ fontSize: 14, color: event.color }}>{event.label}</div>
                            {event.subLabel && <div style={{ fontSize: 12, color: '#888' }}>{event.subLabel}</div>}
                            {event.overlaps && <div style={{ fontSize: 12, color: '#b45309', fontWeight: 600, marginTop: 2 }}>⚠️ Overlaps with another item</div>}  
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <button onClick={() => setShowManualForm(!showManualForm)}
              style={{ padding: '8px 16px', background: showManualForm ? '#e0e7ff' : 'white', border: '2px solid #4f46e5', borderRadius: 8, color: '#4f46e5', cursor: 'pointer', fontWeight: 600 }}>
              + Add Entry Manually
            </button>
          </div>

          {showManualForm && (
            <div style={{ background: '#f9f9f9', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Add Entry Manually</h3>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {['flight', 'lodging', 'activity'].map(t => (
                  <button key={t} onClick={() => set('type', t)}
                    style={{ padding: '6px 14px', borderRadius: 16, border: '2px solid #4f46e5', background: manualEntry.type === t ? '#4f46e5' : 'white', color: manualEntry.type === t ? 'white' : '#4f46e5', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                    {t === 'flight' ? '✈️ Flight' : t === 'lodging' ? '🏨 Lodging' : '🎯 Activity'}
                  </button>
                ))}
              </div>
              {manualEntry.type === 'flight' && (
                <>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input placeholder="Airline" value={manualEntry.airline} onChange={e => set('airline', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                    <input placeholder="Flight #" value={manualEntry.flight_number} onChange={e => set('flight_number', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input placeholder="From (e.g. YYZ)" value={manualEntry.departure_airport} onChange={e => set('departure_airport', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                    <input placeholder="To (e.g. KEF)" value={manualEntry.arrival_airport} onChange={e => set('arrival_airport', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1 }}><label style={{ fontSize: 12, color: '#666' }}>Departure</label><input type="datetime-local" value={manualEntry.departure_time} onChange={e => set('departure_time', e.target.value)} style={inputStyle} /></div>
                    <div style={{ flex: 1 }}><label style={{ fontSize: 12, color: '#666' }}>Arrival</label><input type="datetime-local" value={manualEntry.arrival_time} onChange={e => set('arrival_time', e.target.value)} style={inputStyle} /></div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input placeholder="Confirmation code" value={manualEntry.confirmation_code} onChange={e => set('confirmation_code', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                    <input placeholder="Seat" value={manualEntry.seat} onChange={e => set('seat', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                  </div>
                  <input placeholder="Passenger name" value={manualEntry.passenger_name} onChange={e => set('passenger_name', e.target.value)} style={inputStyle} />
                </>
              )}
              {manualEntry.type === 'lodging' && (
                <>
                  <input placeholder="Hotel / property name" value={manualEntry.name} onChange={e => set('name', e.target.value)} style={inputStyle} />
                  <input placeholder="Address" value={manualEntry.address} onChange={e => set('address', e.target.value)} style={inputStyle} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1 }}><label style={{ fontSize: 12, color: '#666' }}>Check in</label><input type="datetime-local" value={manualEntry.check_in} onChange={e => set('check_in', e.target.value)} style={inputStyle} /></div>
                    <div style={{ flex: 1 }}><label style={{ fontSize: 12, color: '#666' }}>Check out</label><input type="datetime-local" value={manualEntry.check_out} onChange={e => set('check_out', e.target.value)} style={inputStyle} /></div>
                  </div>
                  <input placeholder="Confirmation code" value={manualEntry.confirmation_code} onChange={e => set('confirmation_code', e.target.value)} style={inputStyle} />
                  <input placeholder="Notes" value={manualEntry.notes} onChange={e => set('notes', e.target.value)} style={inputStyle} />
                </>
              )}
              {manualEntry.type === 'activity' && (
                <>
                  <input placeholder="Activity name" value={manualEntry.name} onChange={e => set('name', e.target.value)} style={inputStyle} />
                  <input placeholder="Location" value={manualEntry.location} onChange={e => set('location', e.target.value)} style={inputStyle} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1 }}><label style={{ fontSize: 12, color: '#666' }}>Start time</label><input type="datetime-local" value={manualEntry.start_time} onChange={e => set('start_time', e.target.value)} style={inputStyle} /></div>
                    <div style={{ flex: 1 }}><label style={{ fontSize: 12, color: '#666' }}>End time</label><input type="datetime-local" value={manualEntry.end_time} onChange={e => set('end_time', e.target.value)} style={inputStyle} /></div>
                  </div>
                  <input placeholder="Confirmation code" value={manualEntry.confirmation_code} onChange={e => set('confirmation_code', e.target.value)} style={inputStyle} />
                  <input placeholder="Notes" value={manualEntry.notes} onChange={e => set('notes', e.target.value)} style={inputStyle} />
                </>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button onClick={saveManualEntry} style={{ padding: '8px 20px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Save Entry</button>
                <button onClick={() => { setShowManualForm(false); setManualEntry(EMPTY_MANUAL); }} style={{ padding: '8px 20px', background: '#f5f5f5', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          )}

          <Section title="✈️ Flights">
            {flights.length === 0 ? <Empty text="No flights yet" /> : flights.map(f => (
              <Card key={f.id} onReassign={() => setReassigning({ table: 'flights', id: f.id })} onDelete={() => deleteBooking('flights', f.id)}>
                <strong>{f.airline} {f.flight_number}</strong>
                {f.is_duplicate && <DuplicateWarning />}
                <div style={{ color: '#555', fontSize: 14, marginTop: 4 }}>{f.departure_airport} → {f.arrival_airport}</div>
                <div style={{ color: '#555', fontSize: 14 }}>{formatLocalDateTime(f.departure_time)} → {formatLocalDateTime(f.arrival_time)}</div>
                {f.seat && <div style={{ fontSize: 13, color: '#888' }}>Seat {f.seat}</div>}
                {f.confirmation_code && <div style={{ fontSize: 13, color: '#888' }}>Conf: {f.confirmation_code}</div>}
              </Card>
            ))}
          </Section>

          <Section title="🏨 Lodging">
            {lodging.length === 0 ? <Empty text="No lodging yet" /> : lodging.map(l => (
              <Card key={l.id} onReassign={() => setReassigning({ table: 'lodging', id: l.id })} onDelete={() => deleteBooking('lodging', l.id)}>
                <strong>{l.name}</strong>
                {l.is_duplicate && <DuplicateWarning />}
                {l.address && <div style={{ color: '#555', fontSize: 14, marginTop: 4 }}>{l.address}</div>}
                <div style={{ color: '#555', fontSize: 14 }}>Check in: {formatLocalDateTime(l.check_in)}</div>
                <div style={{ color: '#555', fontSize: 14 }}>Check out: {formatLocalDateTime(l.check_out)}</div>
                {l.confirmation_code && <div style={{ fontSize: 13, color: '#888' }}>Conf: {l.confirmation_code}</div>}
              </Card>
            ))}
          </Section>

          <Section title="🎯 Activities">
            {activities.length === 0 ? <Empty text="No activities yet" /> : activities.map(a => (
              <Card key={a.id} onReassign={() => setReassigning({ table: 'activities', id: a.id })} onDelete={() => deleteBooking('activities', a.id)}>
                <strong>{a.name}</strong>
                {a.is_duplicate && <DuplicateWarning />}
                {a.location && <div style={{ color: '#555', fontSize: 14, marginTop: 4 }}>{a.location}</div>}
                {a.start_time && <div style={{ color: '#555', fontSize: 14 }}>{formatLocalDateTime(a.start_time)}</div>}
                {a.notes && <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{a.notes}</div>}
                {a.confirmation_code && <div style={{ fontSize: 13, color: '#888' }}>Conf: {a.confirmation_code}</div>}
              </Card>
            ))}
          </Section>
        </>
      )}

      {!selectedTrip && !loading && (
        <div style={{ textAlign: 'center', padding: 48, color: '#999' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🗺️</div>
          <div style={{ marginBottom: 20 }}>Create a trip to get started!</div>
          {userProfile && (
            <div style={{ maxWidth: 440, margin: '0 auto', background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 12, padding: 20, textAlign: 'left' }}>
              <div style={{ fontWeight: 700, color: '#1e1b4b', marginBottom: 8 }}>✉️ How Trippit works</div>
              <div style={{ fontSize: 14, color: '#555', lineHeight: 1.5 }}>
                Create a trip above, then forward your booking confirmation emails
                (flights, hotels, restaurants, activities) to your personal address:
              </div>
              <div style={{ margin: '12px 0', padding: '10px 14px', background: 'white', borderRadius: 8, fontWeight: 700, fontSize: 15, color: '#4f46e5', textAlign: 'center' }}>
                {userProfile.trippit_username}@trippit.co
              </div>
              <div style={{ fontSize: 13, color: '#888', lineHeight: 1.5 }}>
                Trippit reads each confirmation and adds it to the right trip
                automatically — no manual entry needed.
              </div>
            </div>
          )}
        </div>
      )}


      {reassigning && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: 300 }}>
            <h3 style={{ margin: '0 0 16px' }}>Move to which trip?</h3>
            {trips.map(trip => (
              <button key={trip.id} onClick={async () => { await reassignBooking(reassigning.table, reassigning.id, trip.id); setReassigning(null); }}
                style={{ display: 'block', width: '100%', padding: '10px 16px', marginBottom: 8, borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', textAlign: 'left', fontWeight: selectedTrip?.id === trip.id ? 600 : 400 }}>
                {trip.name}
              </button>
            ))}
            <button onClick={() => setReassigning(null)} style={{ marginTop: 8, width: '100%', padding: '8px 16px', borderRadius: 8, border: 'none', background: '#f5f5f5', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return <div style={{ marginBottom: 24 }}><h3 style={{ fontSize: 16, marginBottom: 12 }}>{title}</h3>{children}</div>;
}

function Card({ children, onReassign, onDelete }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      {children}
      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        {onReassign && <button onClick={onReassign} style={{ fontSize: 12, color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Move to different trip →</button>}
        {onDelete && <button onClick={onDelete} style={{ fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Delete ×</button>}
      </div>
    </div>
  );
}

function DuplicateWarning() {
  return <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '4px 10px', fontSize: 13, marginTop: 6 }}>⚠️ Possible duplicate booking — check before confirming!</div>;
}

function Empty({ text }) {
  return <div style={{ color: '#aaa', fontSize: 14, padding: '8px 0' }}>{text}</div>;
}

export default App;